import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { GLOBAL_CONFIRMATION_KEY, GLOBAL_TOAST_KEY } from '@core/constants';
import { get } from 'lodash-es';
import { replaceValues } from '@core/utils';

type Messages = Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  $t!: any;

  readonly TOAST_LIFETIME = 5000;

  private http: HttpClient = new HttpClient(inject(HttpBackend));

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {
    this.fetchMessages();
  }

  success(path: string, replacements?: (string | number)[]) {
    this.messageService.add({
      key: GLOBAL_TOAST_KEY,
      life: this.TOAST_LIFETIME,
      severity: 'success',
      summary: this.$t.successHeader,
      detail: this.getMessage(path, replacements),
    });
  }

  error(path: string, replacements?: (string | number)[]) {
    this.messageService.add({
      key: GLOBAL_TOAST_KEY,
      life: this.TOAST_LIFETIME,
      severity: 'error',
      summary: this.$t.errorHeader,
      detail: this.getMessage(path, replacements) || this.$t.generalError,
    });
  }

  errorMessage(message: string) {
    this.messageService.add({
      key: GLOBAL_TOAST_KEY,
      life: this.TOAST_LIFETIME,
      severity: 'error',
      summary: this.$t.errorHeader,
      detail: message || this.$t.generalError,
    });
  }

  confirm(options: Confirmation): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.confirmationService.confirm({
        ...options,
        key: GLOBAL_CONFIRMATION_KEY,
        accept: resolve,
        reject,
      });
    });
  }

  clear() {
    this.messageService.clear(GLOBAL_TOAST_KEY);
  }

  getMessage(path: string, replacements?: (string | number)[]) {
    return replaceValues(get(this.$t, path) || '', replacements);
  }

  private fetchMessages() {
    this.http.get<Messages>('assets/messages.json').subscribe((messages) => (this.$t = messages));
  }
}
